import PropTypes from "prop-types";
import {
  FieldGroup,
  FieldRow,
  FieldLabel,
  TextInput,
  InputPrefix,
  InputSuffix,
} from "../FeeNoticeQueueStyled";
import { formatIsoDate } from "../utils";

/**
 * The full "Create Resolution" screen-flow fee section, replicated per
 * Matthew's 2026-07-24 ruling ("let's at least try that for now").
 *
 * Five fields are reviewer inputs (`amount`, `fee_percent`,
 * `resolution_date`, and the two backend-overridable knobs
 * `referral_fee_percent` / `admin_fee`); everything else
 * (`gross_attorney_fee`, `fees_due_to_others`, `fee_due_to_gordon_partners`,
 * `net_attorney_fee`, `fee_due_date`) is always derived. The parent passes
 * RAW reviewer keystrokes for the editable fields (normalizing them would
 * snap the input back on every keystroke) and the live
 * `feeMath.computeFeePrefills` output for the derived ones. Rendering the
 * derived fields as disabled inputs (rather than plain text) keeps the
 * whole section visually one form while making unmistakable which values
 * a reviewer can actually type into.
 */
const FeeSection = ({ fields, onFieldChange, gordonPartners, disabled }) => (
  <FieldGroup>
    <FieldRow>
      <FieldGroup>
        <FieldLabel>Resolution Date</FieldLabel>
        <TextInput
          type="date"
          value={fields.resolution_date || ""}
          onChange={(e) => onFieldChange("resolution_date", e.target.value)}
          disabled={disabled}
        />
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>Amount</FieldLabel>
        <InputPrefix>
          <span>$</span>
          <TextInput
            inputMode="decimal"
            value={fields.amount ?? ""}
            onChange={(e) => onFieldChange("amount", e.target.value)}
            disabled={disabled}
          />
        </InputPrefix>
      </FieldGroup>
    </FieldRow>

    <FieldRow>
      <FieldGroup>
        <FieldLabel>Fee %</FieldLabel>
        <InputSuffix>
          <TextInput
            inputMode="decimal"
            value={fields.fee_percent ?? ""}
            onChange={(e) => onFieldChange("fee_percent", e.target.value)}
            disabled={disabled}
          />
          <span>%</span>
        </InputSuffix>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>Admin Fee</FieldLabel>
        <InputPrefix>
          <span>$</span>
          <TextInput
            inputMode="decimal"
            value={fields.admin_fee ?? ""}
            onChange={(e) => onFieldChange("admin_fee", e.target.value)}
            disabled={disabled}
          />
        </InputPrefix>
      </FieldGroup>
    </FieldRow>

    <FieldGroup>
      <FieldLabel>Gross Attorney Fee</FieldLabel>
      <InputPrefix>
        <span>$</span>
        <TextInput value={fields.gross_attorney_fee ?? ""} disabled />
      </InputPrefix>
    </FieldGroup>

    <FieldRow>
      <FieldGroup>
        <FieldLabel>Referral Fee %</FieldLabel>
        <InputSuffix>
          <TextInput
            inputMode="decimal"
            value={fields.referral_fee_percent ?? ""}
            onChange={(e) =>
              onFieldChange("referral_fee_percent", e.target.value)
            }
            disabled={disabled}
          />
          <span>%</span>
        </InputSuffix>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>Fees Due to Others</FieldLabel>
        <InputPrefix>
          <span>$</span>
          <TextInput value={fields.fees_due_to_others ?? ""} disabled />
        </InputPrefix>
      </FieldGroup>
    </FieldRow>

    {/* Always visible (even on non-G&P matters, where it stays 0.00) so
        the reviewer sees the complete Resolution field set at a glance
        (Matthew, 2026-07-24). */}
    <FieldGroup>
      <FieldLabel>
        Fee Due to Gordon &amp; Partners{gordonPartners ? "" : " (not a G&P matter)"}
      </FieldLabel>
      <InputPrefix>
        <span>$</span>
        <TextInput
          value={fields.fee_due_to_gordon_partners ?? ""}
          disabled
        />
      </InputPrefix>
    </FieldGroup>

    <FieldGroup>
      <FieldLabel>Net Attorney Fee</FieldLabel>
      <InputPrefix>
        <span>$</span>
        <TextInput value={fields.net_attorney_fee ?? ""} disabled />
      </InputPrefix>
    </FieldGroup>

    <FieldGroup>
      <FieldLabel>Fee Due Date</FieldLabel>
      <TextInput
        value={fields.fee_due_date ? formatIsoDate(fields.fee_due_date) : "—"}
        disabled
      />
    </FieldGroup>

    <FieldGroup>
      <FieldLabel>Referral Partner (Salesforce ID, optional)</FieldLabel>
      <TextInput
        placeholder="e.g. 001Do000..."
        value={fields.referral_partner_id ?? ""}
        onChange={(e) => onFieldChange("referral_partner_id", e.target.value)}
        disabled={disabled}
      />
    </FieldGroup>
  </FieldGroup>
);

FeeSection.propTypes = {
  fields: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  gordonPartners: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default FeeSection;
